import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Col, Container, Row } from "react-bootstrap";
import { Card, CardBody } from "@/shared/components/Card";
import {
  ActionIconContainer,
  AddFormButton,
  CustomLoader,
} from "../../shared/components/TableElements";
import {
  AutoResizeTextArea,
  CheckBoxInput,
  FormContainer,
  FormGroupLabel,
  FormItem,
  HintText,
  Label,
  UnitSpan,
} from "../../shared/components/form/FormElements";
import FormField from "../../shared/components/form/FormField";
import Error from "@/shared/components/form/Error";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select from "react-select";
import { formInputStyles, formStyles } from "../../shared/constants/styles";
import { post, postApi } from "../../api/getUser";
import {
  getDateFnsLocale,
  getDateWithTime,
  getDistanceFactorforOdo,
  getInitialStartTime,
  getTimestamp,
  handleNegative,
  handleNumberInputOnWheelPreventChange,
  odoValidation,
  uploadDocsToS3,
} from "../../shared/helpers";
import getUnixTime from "date-fns/getUnixTime";
import format from "date-fns/format";
import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import ErrorPopup from "../../shared/components/ErrorPopup";
import useAutosizeTextArea from "../../shared/custom-hooks/useAutosizeTextArea";
import DatePicker from "react-datepicker";
import { CustomInput } from "../Reminders/MaintenanceReminders/MaintenanceRemindersForm";
import useFetchOdoHint from "../../shared/custom-hooks/useFetchOdoHint";
import useFetchOdo from "../../shared/custom-hooks/useFetchOdo";
import FileUploaderNew from "../../shared/components/form/FileUploaderNew";
import { NATEXT, s3Url } from "../../shared/constants";
import { IssueStatus } from "./IssuesTable";
import InfoTooltip from "../../shared/components/InfoTooltip";
import { useAuth } from "../../contexts/AuthContext";
import SubscribeTooltipIcon from "../../shared/components/SubscribeTooltipIcon";

const IssuesFormPopup = ({
  showPopup,
  setShowPopup,
  vehicle,
  onNewIssueAdded,
  newIssueName
}) => {
  const { t } = useTranslation("common");
  //const dispatch = useDispatch();
  const { tag } = useAuth();

  const {
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const { orgId, userId, roleId } = useSelector((state) => state.user);
  const userData = useSelector((state) => state.users.list);
  const { distance } = useSelector((state) => state.units);
  const loggedInUser = userData?.find((u) => u.user_id === userId);

  const userListMsg =
    roleId === 1
      ? t("admin_userlist_msg")
      : roleId === 2
        ? t("manager_userlist_msg")
        : "";

  const odometer = watch("odometer");
  const reportedDate = watch("reportedDate");
  const reportedTime = watch("reportedTime");
  const reportedBy = watch("reportedBy");
  const comments = watch("comments");

  const issueDesc = watch("issueDesc");
  const textAreaRef = useRef(null);
  const commentsTextAreaRef = useRef(null);
  useAutosizeTextArea(textAreaRef.current, issueDesc);
  useAutosizeTextArea(commentsTextAreaRef.current, comments);

  const prioritiesOptions = [
    { value: 1, label: t("low") },
    { value: 2, label: t("med") },
    { value: 3, label: t("high") },
  ];

  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [showErrPopup, setShowErrPopup] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [issueImages, setIssueImages] = useState([]);
  const [vehOutOfService, setVehOutOfService] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [showHighOdoErr, setShowHighOdoErr] = useState(false);

  const isEquipment = vehicle?.asset_type === 1;

  const { odoHint, odoUnit, lastOdoValue } = useFetchOdoHint({
    vehicle,
    date: reportedDate,
    time: reportedTime,
  });

  const { odoFetchedMsg, setOdoFetchedMsg } = useFetchOdo({
    vehicle,
    type: "add",
    setValue,
    lastOdoValue,
    setShowHighOdoErr,
  });

  // Initial date/time
  useEffect(() => {
    if (showPopup) {
      setValue("reportedDate", new Date());
      setValue("reportedTime", getInitialStartTime());
    }
  }, [showPopup]);

  //If new issue name is provided, set new issue name
  useEffect(() => {
    if (newIssueName) {
      setValue("issue", newIssueName);
    }
  }, [newIssueName, showPopup]);

  // Auto-set logged-in user as "Reported By" and vehOutOfService from vehicle
  useEffect(() => {
    if (userData?.length > 0 && userId && showPopup) {
      const loggedInUserObj = userData.find((u) => u.user_id === userId);
      if (loggedInUserObj) {
        setValue("reportedBy", loggedInUserObj);
      }
    }
  }, [userId, userData, showPopup]);

  useEffect(() => {
    setVehOutOfService(vehicle?.secondary_status ?? false);
  }, [vehicle]);

  // Fetch user options for Reported By when vehicle is set (non-technician)
  useEffect(() => {
    if (!vehicle || roleId === 4) return;
    (async () => {
      const apiData = {
        veh_id: vehicle.veh_id,
        querystring: "getvehicleoperators",
      };
      const res = await postApi(apiData, "commonNew");
      if (res?.success && res?.user_data?.length > 0) {
        let temp = res.user_data.map((u) => ({
          ...u,
          label: u.name,
          value: u.user_id,
          user_id: u.user_id,
        }));
        if (roleId === 1) {
          const admins = userData?.filter((u) => u.role_id === 1) ?? [];
          const existingUserIds = new Set(temp.map((u) => u.user_id));
          const newAdmins = admins.filter(
            (admin) => !existingUserIds.has(admin.user_id),
          );
          const adminOptions = newAdmins.map((admin) => ({
            ...admin,
            label: admin.name,
            value: admin.user_id,
            user_id: admin.user_id,
          }));
          temp = [...temp, ...adminOptions];
        }
        if (roleId === 1 || roleId === 2) {
          const technicians = userData?.filter((u) => u.role_id === 4) ?? [];
          const existingIds = new Set(temp.map((u) => u.user_id));
          const newTechs = technicians.filter(
            (tech) => !existingIds.has(tech.user_id),
          );
          const techOptions = newTechs.map((tech) => ({
            ...tech,
            label: tech.name,
            value: tech.user_id,
            user_id: tech.user_id,
          }));
          temp = [...temp, ...techOptions];
        }
        setUserOptions(temp);
      }
    })();
  }, [vehicle?.veh_id, roleId, userData]);

  const handleClose = () => {
    reset();
    clearErrors();
    setIssueImages([]);
    setShowPopup(false);
  };

  const onSubmit = async (data) => {
    const {
      issue,
      issueDesc,
      priority,
      reportedBy: reportedByVal,
      odometer: odoVal,
    } = data;
    let reportedDateWithTime = new Date(reportedDate);
    let [hours, minutes] = (reportedTime || getInitialStartTime()).split(":");
    if (hours?.length < 2) hours = hours?.padStart(2, "0");
    if (minutes?.length < 2) minutes = minutes?.padStart(2, "0");
    reportedDateWithTime = setHours(reportedDateWithTime, +hours);
    reportedDateWithTime = setMinutes(reportedDateWithTime, +minutes);

    let odoConverted = isEquipment ? -1000 : 0;

    if (!isEquipment) {
      const distanceFactor = getDistanceFactorforOdo(
        vehicle?.primary_meter,
        distance,
      );
      odoConverted = (odoVal ? parseFloat(odoVal) : 0) * distanceFactor;

      const odoCheckApiData = {
        odo: odoConverted,
        veh_id: vehicle.veh_id,
        datetocheck: getDateWithTime(reportedDate, reportedTime),
        querystring: "get_around_dates_v3",
        check_from: "issues",
        check_id: "",
      };

      const odoValidationRes = await odoValidation(
        "odometer",
        odoCheckApiData,
        setError,
        setSaveBtnLoading,
        t,
        distanceFactor,
        odoVal,
      );

      if (!odoValidationRes) return;
    }

    const issueId = `${vehicle.veh_id}_${Date.now()}`;
    const reportedByUser =
      reportedByVal || userData?.find((u) => u.user_id === userId);

    let imagesToS3 = [];
    let imagesString = "";

    issueImages?.forEach((img, i) => {
      if (img?.hasOwnProperty("existing")) {
        imagesString += `${img?.imgName}:::`;
      } else if (img?.file) {
        const extension = img?.file?.name?.split(".").pop();
        const imgName = `${getTimestamp(userId, i)}.${extension}`;
        const key = `image_data/org_${orgId}/issues/${issueId}/${imgName}`;
        imagesString += `${imgName}:::`;
        imagesToS3.push({ ...img, key });
      }
    });
    imagesString = imagesString.slice(0, imagesString.length - 3);

    const source = t("added_via_website_by_user", {
      userName: reportedByUser?.name || "",
    });

    let priorityValue = priority?.value ?? 0;
    if (roleId === 4 && !loggedInUser?.allow_wo) {
      priorityValue = 0;
    }

    const apiData = {
      querystring: "addEditIssues",
      operation: "create",
      issue_id: issueId,
      org_id: orgId,
      veh_id: vehicle.veh_id,
      odo: isEquipment ? -1000 : odoConverted,
      issue: issue ?? "",
      issue_desc: issueDesc ?? "",
      reported_by: reportedByUser?.user_id ?? "",
      reported_date: getUnixTime(reportedDateWithTime),
      close_date: "",
      source,
      status: 0,
      priority: priorityValue,
      comments: comments ?? "",
      inspection_id: "",
      work_order_id: "",
      service_id: "",
      issue_img_name: imagesString,
      action: 1,
      formatted_date: format(reportedDateWithTime, "dd MMM yyyy"),
    };

    const vehicleApiData = {
      veh_id: vehicle?.veh_id,
      secondary_status: vehOutOfService ? 1 : 0,
      operation: "update",
      asset_type: vehicle?.asset_type,
    };

    setSaveBtnLoading(true);

    const vehicleRes = await post("vehicle", vehicleApiData);

    if (!vehicleRes?.success) {
      setSaveBtnLoading(false);
      setShowErrPopup(true);
      setErrMsg(t("form_err_msg"));
      return;
    }

    const response = await postApi(apiData, "issue");

    if (response?.success === 1) {
      if (imagesToS3?.length > 0) {
        const docsUploaded = await uploadDocsToS3(imagesToS3);
        if (docsUploaded) {
          //dispatch(addMsg(t("record_added_msg", { recordName: t("issue") })));
          setSaveBtnLoading(false);
          reset();
          setIssueImages([]);
          setShowPopup(false);
          if (onNewIssueAdded) await onNewIssueAdded(issueId, issue);
        }
      } else {
        //dispatch(addMsg(t("record_added_msg", { recordName: t("issue") })));
        setSaveBtnLoading(false);
        reset();
        setIssueImages([]);
        setShowPopup(false);
        if (onNewIssueAdded) await onNewIssueAdded(issueId, issue);
      }
    } else {
      setSaveBtnLoading(false);
      setErrMsg(
        response?.action_value === "record_exists"
          ? t("dup_issue_err_msg")
          : t("form_err_msg"),
      );
      setShowErrPopup(true);
    }
  };

  if (!vehicle) return null;

  return (
    <>
      {showErrPopup && (
        <ErrorPopup
          showPopup={showErrPopup}
          setShowPopup={setShowErrPopup}
          message={errMsg}
        />
      )}
      <Modal show={showPopup} size="xl" backdropClassName="custom-backdrop">
        <Modal.Header>
          <h4>{t("add_issue")}</h4>
          <div className="d-flex justify-content-end">
            <ActionIconContainer onClick={handleClose}>
              <i className="fi fi-rr-cross-small" />
            </ActionIconContainer>
          </div>
        </Modal.Header>
        <Modal.Body className="m-0 py-0 px-2">
          <FormContainer className="m-0 p-0">
            <Container className="m-0 p-0">
              <Card>
                <CardBody
                  style={{
                    height: "calc(var(--window-height, 100vh) - 280px)",
                    overflow: "auto",
                    //paddingBottom: "1.5rem",
                  }}
                >
                  <Row>
                    {/* Left column: main fields */}
                    <Col md={6}>
                      {/* Vehicle (read-only) */}
                      <Row>
                        <Col md={6}>
                          <FormGroupLabel>{t("asset")}</FormGroupLabel>
                          <FormItem disabled>
                            <input
                              type="text"
                              value={vehicle?.label || vehicle?.veh_name || ""}
                              disabled
                              style={{
                                ...formInputStyles,
                                background: "#f5f5f5",
                              }}
                            />
                          </FormItem>
                        </Col>
                        {/* Odometer */}
                        {!isEquipment && (
                          <Col>
                            <FormGroupLabel>{t("odometer")}</FormGroupLabel>
                            <FormItem showWarning={showHighOdoErr}>
                              <FormField
                                name="odometer"
                                control={control}
                                component="input"
                                type="number"
                                onKeyDown={handleNegative}
                                onWheel={handleNumberInputOnWheelPreventChange}
                                rules={{ required: t("required_err") }}
                                placeholder={t("odometer")}
                                style={formInputStyles}
                                onChange={(e) => {
                                  clearErrors("odometer");
                                  setValue("odometer", e.target.value);
                                  if (odoFetchedMsg) setOdoFetchedMsg("");
                                }}
                                onKeyUp={(e) => {
                                  const val = e.target?.value;
                                  if (
                                    val &&
                                    lastOdoValue &&
                                    +val - +lastOdoValue > 1000
                                  ) {
                                    setShowHighOdoErr(true);
                                  } else {
                                    setShowHighOdoErr(false);
                                  }
                                }}
                              />
                              <UnitSpan>{odoUnit}</UnitSpan>
                            </FormItem>
                            {odoFetchedMsg && (
                              <HintText>{odoFetchedMsg}</HintText>
                            )}
                            {odoHint && <HintText>{odoHint}</HintText>}
                            {showHighOdoErr && (
                              <Error error={t("high_odo_err_msg")} />
                            )}
                            {errors?.odometer && (
                              <Error error={errors.odometer.message} />
                            )}
                          </Col>
                        )}
                      </Row>

                      <Row className="pt-2">
                        <Col md={6}>
                          <FormGroupLabel>{t("issue")}</FormGroupLabel>
                          <FormItem>
                            <FormField
                              name="issue"
                              control={control}
                              component="input"
                              defaultValue=""
                              placeholder={t("issue_name")}
                              style={formInputStyles}
                              rules={{ required: t("required_err") }}
                            />
                          </FormItem>
                          {errors?.issue && (
                            <Error error={errors.issue.message} />
                          )}
                        </Col>
                      </Row>

                      <Row>
                        <Col>
                          <FormGroupLabel>{t("issue_desc")}</FormGroupLabel>
                          <FormItem disabled={status == 2 && roleId !== 1}>
                            <FormField
                              name="issueDesc"
                              control={control}
                              component="input"
                              type="text"
                              defaultValue=""
                              placeholder={t("issue_desc_placeholder")}
                              style={formInputStyles}
                              disabled={status == 2 && roleId !== 1}
                            />

                            {/* {errors?.[name] && <Error error={errors[name].message} top={true} />} */}
                          </FormItem>

                          {errors?.issueDesc && (
                            <Error error={errors.issueDesc.message} />
                          )}
                        </Col>
                      </Row>

                      <Row className="pt-2">
                        <Col>
                          <FormGroupLabel>{t("reported_date")}</FormGroupLabel>
                          <Controller
                            name="reportedDate"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, value } }) => (
                              <DatePicker
                                id="datepicker-popup"
                                locale={getDateFnsLocale()}
                                dateFormat="P"
                                dropdownMode="scroll"
                                maxDate={new Date()}
                                showMonthDropdown
                                showYearDropdown
                                selected={value}
                                onChange={onChange}
                                customInput={<CustomInput />}
                                placeholderText={t("date_tv")}
                              />
                            )}
                            rules={{ required: t("required_err") }}
                          />
                          {errors?.reportedDate && (
                            <Error error={errors.reportedDate.message} />
                          )}
                        </Col>
                        <Col>
                          <FormGroupLabel>{t("reported_time")}</FormGroupLabel>
                          <FormItem>
                            <FormField
                              name="reportedTime"
                              control={control}
                              component="input"
                              type="time"
                              style={formInputStyles}
                            />
                            {errors?.reportedTime && (
                              <Error error={errors.reportedTime.message} />
                            )}
                          </FormItem>
                        </Col>
                      </Row>

                      <Row className="pt-2">
                        <Col>
                          <FormGroupLabel>{t("reported_by")}</FormGroupLabel>
                          {userListMsg && (
                            <InfoTooltip
                              tooltipText={userListMsg}
                              iconStyles={{ margin: "0 4px" }}
                            />
                          )}
                          <Controller
                            name="reportedBy"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <Select
                                options={
                                  roleId === 4
                                    ? userData?.map((u) => ({
                                        ...u,
                                        label: u.name,
                                        value: u.user_id,
                                      }))
                                    : userOptions?.length
                                      ? userOptions
                                      : (userData?.map((u) => ({
                                          ...u,
                                          label: u.name,
                                          value: u.user_id,
                                        })) ?? [])
                                }
                                value={value}
                                onChange={onChange}
                                placeholder={t("user_placeholder")}
                                styles={formStyles}
                                isDisabled={roleId === 4}
                                isClearable={roleId !== 4}
                              />
                            )}
                            rules={{ required: t("required_err") }}
                          />
                          {errors?.reportedBy && (
                            <Error error={errors.reportedBy.message} />
                          )}
                        </Col>
                        <Col>
                          <FormGroupLabel className="mt-3">
                            {t("source")}
                          </FormGroupLabel>
                          <div
                            style={{
                              height: "34px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <p className="m-0 p-0">
                              {reportedBy
                                ? t("added_via_website_by_user", {
                                    userName: reportedBy?.name,
                                  })
                                : NATEXT}
                            </p>
                          </div>
                        </Col>
                      </Row>

                      <Row className="pt-2">
                        <Col>
                          <FormGroupLabel>{t("notes_tv")}</FormGroupLabel>
                          <Controller
                            name="comments"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <AutoResizeTextArea
                                ref={commentsTextAreaRef}
                                onChange={onChange}
                                value={value}
                                placeholder={t("notes_tv")}
                                rows={1}
                                //style={{ minHeight: "70px", resize: "vertical" }}
                              />
                            )}
                          />
                          {errors?.comments && (
                            <Error error={errors.comments.message} />
                          )}
                        </Col>
                      </Row>

                      <Row className="px-1 pt-2 pb-4">
                        <FormGroupLabel>
                          {t("attachments")}{" "}
                          {tag === "free-user" && (
                            <SubscribeTooltipIcon
                              message={t("subscribe_msg")}
                            />
                          )}
                        </FormGroupLabel>
                        <Col>
                          <FileUploaderNew
                            disabled={false}
                            images={issueImages}
                            setImages={setIssueImages}
                            shouldDelete={true}
                          />
                        </Col>
                      </Row>
                    </Col>

                    {/* Right column: status, priority, out of service */}
                    <Col md={2} />
                    <Col md={4}>
                      <Row>
                        <Col style={{ marginBottom: "0.5rem" }}>
                          <FormGroupLabel className="pb-2">
                            {t("status")}
                          </FormGroupLabel>
                          <IssueStatus status="new" />
                        </Col>
                      </Row>
                      {(roleId !== 4 ||
                        (roleId === 4 && !!loggedInUser?.allow_wo)) && (
                        <Row>
                          <Col md={6}>
                            <FormGroupLabel>{t("priority")}</FormGroupLabel>
                            <Controller
                              name="priority"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <Select
                                  options={prioritiesOptions}
                                  value={value}
                                  onChange={onChange}
                                  placeholder={t("priority_placeholder")}
                                  styles={formStyles}
                                  isClearable
                                />
                              )}
                              rules={{ required: t("required_err") }}
                            />
                            {errors?.priority && (
                              <Error error={errors.priority.message} />
                            )}
                          </Col>
                        </Row>
                      )}
                      <Row className="mt-4">
                        <Col>
                          <CheckBoxInput
                            type="checkbox"
                            id="vehOutOfServicePopup"
                            name="vehOutOfService"
                            value="vehOutOfService"
                            checked={vehOutOfService}
                            onChange={() => setVehOutOfService((prev) => !prev)}
                            disabled
                            style={{ marginRight: "0.5rem" }}
                          />
                          <Label htmlFor="vehOutOfServicePopup" disabled={true}>
                            {t("asset_out_of_service_label")}
                          </Label>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Container>
          </FormContainer>
        </Modal.Body>
        <Modal.Footer>
          <AddFormButton
            id="save-issue-popup-button"
            type="button"
            onClick={handleSubmit(onSubmit)}
          >
            {saveBtnLoading ? <CustomLoader /> : t("save")}
          </AddFormButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default IssuesFormPopup;
